/* eslint-disable @typescript-eslint/no-var-requires */
import { getRandomIntInclusive, useSoundPlayer } from "Helpers";
import React, { useState, useEffect, useCallback } from "react";

import explosion from "./sfx/explosion.wav";
import click1SFX from "./sfx/click-1.wav";
import click2SFX from "./sfx/click-2.wav";
import bigRevealSFX from "./sfx/big-reveal.wav";
import bigReveal2SFX from "./sfx/big-reveal-2.wav";
import playSFX from "./sfx/play.wav";
import play2SFX from "./sfx/play-2.wav";

const playSounds = [playSFX, play2SFX];
const clickSounds = [click1SFX, click2SFX];
const revealSounds = [bigRevealSFX, bigReveal2SFX];

type GameStatusProps = {
  started: boolean;
  lost: boolean;
  minesCount: number;
  flagsCount: number;
  buttonEmoji: "🙂" | "😵" | "😎";
};
type CellProps = {
  isMined: boolean;
  isFlagged: boolean;
  clicked: boolean;
  revealed: boolean;
  nearMines: number;
  animationDelay: number;
};
const useMineSweeper = (
  CELL_SIZE: number,
  sound: boolean,
  size = 8,
  bombs = 8
) => {
  const soundPlayer = useSoundPlayer();
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
  const [timerValue, setTimerValue] = useState<number>(0);
  const [recursiveCount, setRecursiveCount] = useState(0);
  const [gameStatus, setGameStatus] = useState<GameStatusProps>({
    started: true,
    lost: false,
    minesCount: Math.min(bombs, size * 2),
    flagsCount: 0,
    buttonEmoji: "🙂"
  });
  const [grid, setGrid] = useState<CellProps[][]>(
    new Array(size).fill(
      new Array(size).fill({
        isMined: false,
        isFlagged: false,
        clicked: true,
        nearMines: 0,
        animationDelay: 0
      })
    )
  );

  function PlaySound(snd: string) {
    if (!sound) return;
    soundPlayer.play(snd);
  }

  function startGame() {
    PlaySound(playSounds[getRandomIntInclusive(0, playSounds.length - 1)]);
    const newGrid: CellProps[][] = new Array(0);
    const cell: CellProps = {
      isMined: false,
      isFlagged: false,
      clicked: false,
      revealed: false,
      nearMines: 0,
      animationDelay: 0
    };
    // Se crea el tablero con las celdas inicializadas
    for (let x = 0; x < size; x++) {
      newGrid.push([]);
      for (let y = 0; y < size; y++) {
        newGrid[x].push({ ...cell });
      }
    }
    // Se asignan las minas en posiciones aleatorias
    for (let index = 0; index < Math.min(bombs, size * 2); index++) {
      const { x, y } = getRandomCell(newGrid);
      newGrid[x][y].isMined = true;
    }

    setTimerValue(0);
    stopTimer();
    setGrid([...newGrid]);
    setGameStatus({
      started: true,
      lost: false,
      minesCount: Math.min(bombs, size * 2),
      flagsCount: 0,
      buttonEmoji: "🙂"
    });
  }

  function onGameOver(win: boolean) {
    setGameStatus(prev => ({
      ...prev,
      lost: !win,
      started: false,
      buttonEmoji: win ? "😎" : "😵"
    }));
    if (!win) PlaySound(explosion);
    stopTimer();
  }

  function checkCompletion() {
    const result =
      grid.filter(
        row => row.filter(cell => !cell.clicked && !cell.isMined).length > 0
      ).length > 0;
    if (!result) onGameOver(true);
  }

  const startTimer = useCallback(() => {
    if (!timer)
      setTimer(setInterval(() => setTimerValue(prev => prev + 1), 1000));
  }, [timer]);

  function stopTimer() {
    setTimer(prev => {
      if (prev) clearInterval(prev);
      return null;
    });
  }

  const handleCellClick = (x: number, y: number) => {
    startTimer();
    const cell = grid[x][y];
    // Si se toca una mina, se muestra el resto
    if (cell.isMined) {
      onGameOver(false);
      setGrid([
        ...grid.map(col =>
          col.map(c =>
            c.isMined ? { ...c, clicked: true, revealed: true } : c
          )
        )
      ]);
      return;
    }
    // Comprobamos las casillas adyacentes y asignamos los valores que retorna la funcion
    setRecursiveCount(0);
    setGrid([...checkAround(x, y, grid)]);
    checkCompletion();
  };

  // Funcion para poner las banderitas
  const handleCellAuxClick = (x: number, y: number, flag: boolean) => {
    const cell = grid[x][y];
    setGameStatus(prev => {
      if (prev.flagsCount >= prev.minesCount && flag) return prev;

      cell.isFlagged = flag;
      return { ...prev, flagsCount: prev.flagsCount + (flag ? 1 : -1) };
    });
    setGrid([...grid]);
  };

  function getRandomCell(
    _grid: CellProps[][],
    isMined = false
  ): { x: number; y: number } {
    const x = getRandomIntInclusive(0, _grid.length - 1);
    const y = getRandomIntInclusive(0, _grid[x].length - 1);
    if (_grid[x][y].isMined !== isMined) return getRandomCell(_grid);
    return { x, y };
  }

  function checkNearMines(x: number, y: number) {
    let mineCount = 0;
    for (let _x = Math.max(x - 1, 0); _x <= Math.min(x + 1, size); _x++) {
      for (let _y = Math.max(y - 1, 0); _y <= Math.min(y + 1, size); _y++) {
        // Comprobamos que la casilla es valida para sumar la cantidad de minas
        if (!(_x >= grid.length || _y >= grid?.[_x].length)) {
          const _cell = grid[_x][_y];
          if (_cell && _cell.isMined) mineCount++;
        }
      }
    }
    return mineCount;
  }

  // Funcion para comprobar casillas cercanas
  function checkAround(
    x: number,
    y: number,
    _grid: CellProps[][]
  ): CellProps[][] {
    const _cell = _grid[x][y];
    _cell.clicked = true;
    setRecursiveCount(prev => {
      prev > 0
        ? PlaySound(
            revealSounds[getRandomIntInclusive(0, revealSounds.length - 1)]
          )
        : PlaySound(
            clickSounds[getRandomIntInclusive(0, clickSounds.length - 1)]
          );
      _cell.animationDelay = prev;
      return prev + 1;
    });
    // Se establecen los valores para las casillas cercanas a las minas
    _cell.nearMines = checkNearMines(x, y);
    // Si la casilla actual no tiene minas cerca, comprobamos alrededor
    if (_cell.nearMines <= 0)
      for (let i = Math.max(x - 1, 0); i <= Math.min(x + 1, size); i++) {
        for (let j = Math.max(y - 1, 0); j <= Math.min(y + 1, size); j++) {
          // Comprobamos que la casilla cercana es valida para usar la recursividad
          if (!(i >= grid.length || j >= grid?.[i].length)) {
            const cell = _grid[i][j];
            if (!cell.isFlagged && !cell.isMined && !cell.clicked)
              checkAround(i, j, _grid);
          }
        }
      }
    return _grid;
  }

  useEffect(() => {
    startGame();
  }, []);

  const blockRender = useCallback(
    (x: number, y: number) => {
      const cell = grid[x][y];
      return (
        <>
          {(cell.clicked || cell.revealed) &&
            (cell.nearMines > 0 || cell.isMined) && (
              <p
                className={`number n${cell.nearMines}`}
                style={{
                  fontSize: CELL_SIZE - (cell.isMined ? 8 : 0),
                  lineHeight: `${CELL_SIZE}px`
                }}>
                {cell.isMined ? "💣" : cell.nearMines}
              </p>
            )}
          <button
            className={`block${cell.clicked ? " clicked" : ""}${
              cell.revealed ? " revealed" : ""
            }${
              !gameStatus.started && !gameStatus.lost && cell.isMined
                ? " red"
                : ""
            }`}
            style={{
              pointerEvents:
                gameStatus.started && !cell.clicked ? "auto" : "none",
              transitionDelay: `${
                (cell.animationDelay * 500) / recursiveCount
              }ms`
            }}
            onContextMenu={e => {
              e.preventDefault();
              handleCellAuxClick(x, y, !cell.isFlagged);
            }}
            onClick={() => handleCellClick(x, y)}
          />

          {!cell.clicked && (
            <p
              className='flag'
              style={{
                fontSize: CELL_SIZE - 12,
                lineHeight: `${CELL_SIZE}px`
              }}>
              {cell.isFlagged && gameStatus.started && "🚩"}
              {!gameStatus.started && !gameStatus.lost && cell.isMined && "💣"}
            </p>
          )}
        </>
      );
    },
    [grid, gameStatus.started, gameStatus.lost, recursiveCount, CELL_SIZE]
  );

  return { CELL_SIZE, grid, gameStatus, timerValue, blockRender, startGame };
};

export default useMineSweeper;
